import { EstimateData } from '../../services/estimateService';
import { SHIPPING_METHOD_LABEL, ShippingMethod, FREE_REVISION_LIMIT } from '../../types/orderPolicy';
import styles from './Estimate.module.scss';

// 견적서 공급자 정보 (Footer의 사업자 정보와 같아야 한다)
const SUPPLIER = {
  name: 'G-WORKS',
  ceo: '변수영',
  bizNo: '655-88-00579',
  address: '경상북도 구미시 선산읍 선주로 129-60',
  tel: '053-216-4126',
  email: 'gn-co@daum.net',
};

const VALID_DAYS = 30;

function formatDate(value: string | Date): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
}

function won(value: number): string {
  return `${value.toLocaleString()}원`;
}

// 결제금액(total_price)은 부가세 포함 금액으로 보고 공급가액/부가세로 나눠 표기한다
function splitVat(total: number): { supply: number; vat: number } {
  const supply = Math.round(total / 1.1);
  return { supply, vat: total - supply };
}

function EstimateDocument({ data }: { data: EstimateData }) {
  const issued = new Date(data.issuedAt);
  const validUntil = new Date(issued.getTime() + VALID_DAYS * 24 * 60 * 60 * 1000);
  const itemTotal = data.totalPrice - data.revisionFee;
  const { supply, vat } = splitVat(data.totalPrice);
  const shipping = SHIPPING_METHOD_LABEL[data.shippingMethod as ShippingMethod] ?? '단건발송';

  return (
    <article className={styles.sheet} aria-label="견적서">
      <h1 className={styles.sheetTitle}>견 적 서</h1>

      <div className={styles.meta}>
        <dl className={styles.recipient}>
          <div>
            <dt>견적번호</dt>
            <dd>{data.orderNo}</dd>
          </div>
          <div>
            <dt>견적일자</dt>
            <dd>{formatDate(issued)}</dd>
          </div>
          <div>
            <dt>수신</dt>
            <dd>
              {data.company ? `${data.company} ` : ''}
              {data.customerName} 귀하
            </dd>
          </div>
          {data.title && (
            <div>
              <dt>건명</dt>
              <dd>{data.title}</dd>
            </div>
          )}
          <div>
            <dt>유효기간</dt>
            <dd>{formatDate(validUntil)}까지</dd>
          </div>
        </dl>

        <table className={styles.supplier}>
          <caption>공급자</caption>
          <tbody>
            <tr>
              <th>상호</th>
              <td>{SUPPLIER.name}</td>
              <th>대표</th>
              <td>{SUPPLIER.ceo}</td>
            </tr>
            <tr>
              <th>사업자번호</th>
              <td colSpan={3}>{SUPPLIER.bizNo}</td>
            </tr>
            <tr>
              <th>주소</th>
              <td colSpan={3}>{SUPPLIER.address}</td>
            </tr>
            <tr>
              <th>연락처</th>
              <td colSpan={3}>
                {SUPPLIER.tel} · {SUPPLIER.email}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className={styles.totalLine}>
        합계금액 <strong>{won(data.totalPrice)}</strong> <span>(부가세 포함)</span>
      </p>

      <table className={styles.items}>
        <thead>
          <tr>
            <th>No</th>
            <th>품목</th>
            <th>규격</th>
            <th>수량</th>
            <th>금액</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td>{data.shape ?? '-'}</td>
            <td>{[data.boxSize, data.printOption].filter(Boolean).join(' / ') || '-'}</td>
            <td>{data.quantity.toLocaleString()}</td>
            <td className={styles.num}>{won(itemTotal)}</td>
          </tr>
          {data.revisionFee > 0 && (
            <tr>
              <td>2</td>
              <td>추가 시안 수정비</td>
              <td>무료 {FREE_REVISION_LIMIT}회 초과분</td>
              <td>-</td>
              <td className={styles.num}>{won(data.revisionFee)}</td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={4}>공급가액</th>
            <td className={styles.num}>{won(supply)}</td>
          </tr>
          <tr>
            <th colSpan={4}>부가세</th>
            <td className={styles.num}>{won(vat)}</td>
          </tr>
          <tr className={styles.grand}>
            <th colSpan={4}>합계</th>
            <td className={styles.num}>{won(data.totalPrice)}</td>
          </tr>
        </tfoot>
      </table>

      <ul className={styles.notes}>
        <li>배송 방법: {shipping}. 배송비는 배송 형태 및 지역에 따라 별도 안내드립니다.</li>
        {data.deadline && <li>납기 희망일: {formatDate(data.deadline)}</li>}
        <li>
          시안 수정은 수정요청 양식 기준 {FREE_REVISION_LIMIT}회까지 무료이며, 이후 추가 수정은 유료로 결제금액에 합산됩니다.
        </li>
        <li>본 견적서는 발행일로부터 {VALID_DAYS}일간 유효하며, 사양 변경 시 금액이 달라질 수 있습니다.</li>
      </ul>

      <p className={styles.sign}>위와 같이 견적합니다. &nbsp; {SUPPLIER.name}</p>
    </article>
  );
}

export default EstimateDocument;
